const MEDIA_SOURCE_QUALITY_REASONS = new Set([
    "classification_unknown",
    "fenix_coverage_missing",
    "fenix_coverage_partial",
    "fenix_current_day_validated_partial",
    "fenix_unavailable",
    "source_identity_ambiguous",
    "fenix_generation_mismatch",
    "media_source_quality_unknown",
]);

/** Missing fields stay legacy/unknown; only an explicitly applicable gap blocks metrics. */
export function normalizeMediaSourceQuality(quality) {
    if (quality === null || typeof quality !== "object" || typeof quality.applicable !== "boolean") {
        return null;
    }

    if (!quality.applicable) {
        return {version: quality.version, applicable: false, available: true, complete: true, issues: [], currentDayPartialScopeExplained: true};
    }

    const rawIssues = Array.isArray(quality.issues) ? quality.issues : null;
    const issues = Array.isArray(quality.issues)
        ? quality.issues.filter((issue) => issue && MEDIA_SOURCE_QUALITY_REASONS.has(issue.reason))
        : [];

    return {
        version: quality.version,
        applicable: true,
        available: quality.available === true,
        complete: quality.complete === true,
        issues,
        currentDayPartialScopeExplained: typeof quality.currentDayPartialScopeExplained === "boolean"
            ? quality.currentDayPartialScopeExplained
            : quality.complete === true || (quality.available === true
                && rawIssues !== null
                && rawIssues.length > 0
                && rawIssues.every((issue) => issue !== null && typeof issue === "object" && !Array.isArray(issue)
                    && MEDIA_SOURCE_QUALITY_REASONS.has(issue.reason))),
    };
}

export function mediaSourceQualityHasValidatedCurrentDayPartial(quality, {now = new Date(), timeZone = "Europe/Prague"} = {}) {
    // This exception is deliberately stricter than normalized display quality:
    // normalization drops unknown issues, but an unknown/malformed issue must
    // never be hidden by the current-day exception.
    if (quality === null || typeof quality !== "object"
        || quality.version !== 1
        || quality.applicable !== true
        || quality.available !== true
        || quality.complete !== false
        || (quality.currentDayPartialScopeExplained === false)
        || !Array.isArray(quality.issues)
        || quality.issues.length === 0) {
        return false;
    }

    const today = publicationTodayKey(now, timeZone);
    return quality.issues.every((issue) => issue !== null && typeof issue === "object" && !Array.isArray(issue)
        && issue.reason === "fenix_current_day_validated_partial"
        && publicationDateKey(issue.day) === today
        && typeof issue.integrationId === "string" && issue.integrationId.trim() !== ""
        && typeof issue.accountId === "string" && issue.accountId.trim() !== ""
        && Number.isSafeInteger(issue.premiseId) && issue.premiseId > 0);
}

export function mediaSourceQualityBlocksMetrics(quality, options) {
    const normalized = normalizeMediaSourceQuality(quality);
    if (mediaSourceQualityHasValidatedCurrentDayPartial(quality, options)) return false;
    return normalized?.applicable === true && (normalized.available !== true || normalized.complete !== true);
}

function snapshotHasOnlyValidatedCurrentDayPartial(snapshot, {now = new Date()} = {}) {
    if (!Array.isArray(snapshot?.confirmedDataAbsentDays) || snapshot.confirmedDataAbsentDays.length > 0
        || !Array.isArray(snapshot?.missingDays)) {
        return false;
    }

    const today = publicationTodayKey(now);
    const missingDays = snapshot.missingDays.map(publicationDateKey);
    return missingDays.every((day) => day === today)
        && mediaSourceQualityHasValidatedCurrentDayPartial(snapshot?.mediaSourceQuality, {now});
}

export function isSnapshotPeriodIncomplete(snapshot, options) {
    return snapshot?.complete === false && !snapshotHasOnlyValidatedCurrentDayPartial(snapshot, options);
}

export function snapshotHasPublishedDays(snapshot) {
    return hasAvailableSummary(snapshot);
}

export function hasAvailableSummary(snapshot) {
    const days = publishedSnapshotDayKeys(snapshot);
    return Array.isArray(days) && days.length > 0;
}

export function isSnapshotSummaryUnavailable(snapshot) {
    return !hasAvailableSummary(snapshot);
}

export function snapshotRowsForPublishedDays(rows, snapshot) {
    return hasAvailableSummary(snapshot) ? rows : [];
}

export function snapshotHasPublishedSubset(snapshots) {
    return (Array.isArray(snapshots) ? snapshots : [snapshots])
        .some((snapshot) => isSnapshotPeriodIncomplete(snapshot) && snapshotHasPublishedDays(snapshot));
}

export function isSnapshotDateUnavailable(snapshot, date, options) {
    const today = publicationTodayKey(options?.now);
    if (date === today && snapshotHasOnlyValidatedCurrentDayPartial(snapshot, options)) return false;
    if (Array.isArray(snapshot?.confirmedDataAbsentDays) && snapshot.confirmedDataAbsentDays.map(publicationDateKey).includes(date)) return true;
    if (validReportingExcludedDays(snapshot).includes(date)) {
        return true;
    }

    if (!isSnapshotPeriodIncomplete(snapshot, options)) {
        return false;
    }

    if (!Array.isArray(snapshot.missingDays)) {
        return true;
    }

    return snapshot.missingDays.includes(date);
}

export function withSnapshotMissingDays(snapshot, dates = [], options) {
    const currentDayIsUsable = snapshotHasOnlyValidatedCurrentDayPartial(snapshot, options);
    const today = publicationTodayKey(options?.now);
    const missingDays = isSnapshotPeriodIncomplete(snapshot, options) && Array.isArray(snapshot.missingDays)
        ? snapshot.missingDays
        : [];

    return [...new Set([...dates, ...missingDays, ...validReportingExcludedDays(snapshot, options)])]
        .filter((date) => !(currentDayIsUsable && date === today))
        .sort((left, right) => left.localeCompare(right));
}

export function snapshotDateValue(value, snapshot, date) {
    return isSnapshotDateUnavailable(snapshot, date) ? null : value;
}

export function snapshotPeriodValue(value, snapshot) {
    return isSnapshotSummaryUnavailable(snapshot) ? null : value;
}

export function snapshotComparisonValue(value, currentSnapshot, comparisonSnapshot) {
    return isSnapshotSummaryUnavailable(currentSnapshot) || isSnapshotSummaryUnavailable(comparisonSnapshot)
        ? null
        : value;
}

function validPublicationDateKey(value) {
    if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const parsed = new Date(`${value}T12:00:00Z`);
    return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

export function publicationTodayKey(now = new Date(), timeZone = "Europe/Prague") {
    const parts = new Intl.DateTimeFormat("en-CA", {timeZone, year: "numeric", month: "2-digit", day: "2-digit"}).formatToParts(now);
    return ["year", "month", "day"].map((type) => parts.find((part) => part.type === type)?.value).join("-");
}

/** Publication metadata, never order counts or advertising amounts, determines missing days. */
export function classifySnapshotPublication(snapshot, {now = new Date(), timeZone = "Europe/Prague"} = {}) {
    const today = publicationTodayKey(now, timeZone);
    const incomplete = isSnapshotPeriodIncomplete(snapshot);
    const suppliedDays = Array.isArray(snapshot?.missingDays) ? snapshot.missingDays : null;
    const missingDays = suppliedDays ? [...new Set(suppliedDays.filter((day) => validPublicationDateKey(day)))].sort() : [];
    const unavailableDays = [...new Set([...missingDays, ...validReportingExcludedDays(snapshot)])].sort();
    const historicalMissingDays = incomplete ? unavailableDays.filter((day) => day < today) : [];
    const currentDayPending = incomplete && unavailableDays.includes(today);
    const unknownMissingDays = incomplete && unavailableDays.length === 0 && (!suppliedDays || suppliedDays.length === 0 || missingDays.length !== new Set(suppliedDays).size);
    return {today, historicalMissingDays, currentDayPending, unknownMissingDays, hasHistoricalGap: historicalMissingDays.length > 0 || unknownMissingDays};
}

export function formatPublicationDate(value) {
    const key = value instanceof Date ? (Number.isFinite(value.getTime()) ? value.toISOString().slice(0, 10) : "") : typeof value === "string" ? value.slice(0, 10) : "";
    if (!validPublicationDateKey(key)) return null;
    const [year, month, day] = key.split("-").map(Number);
    return `${day}.${month}.${year}`;
}

function publicationDateKey(value) {
    if (value instanceof Date) {
        return Number.isFinite(value.getTime()) ? value.toISOString().slice(0, 10) : null;
    }
    const key = typeof value === "string" ? value.slice(0, 10) : "";
    return validPublicationDateKey(key) ? key : null;
}

function validReportingExcludedDays(snapshot, {now = new Date()} = {}) {
    if (snapshot?.rangeMismatch === true) return [];
    const suppliedDays = Array.isArray(snapshot?.reportingExcludedDays) ? snapshot.reportingExcludedDays : [];
    const requestDates = publicationDaysBetween(publicationDateKey(snapshot?.requestedSince), publicationDateKey(snapshot?.requestedUntil));
    if (!requestDates) return [];
    const today = publicationTodayKey(now);
    const currentDayIsUsable = snapshotHasOnlyValidatedCurrentDayPartial(snapshot, {now});
    return [...new Set(suppliedDays.map(publicationDateKey).filter((day) => day
        && requestDates.includes(day)
        && !(day === today && currentDayIsUsable)))].sort();
}

/** Trims only a trailing excluded reporting window for date-range detail endpoints. */
export function effectiveReportingUntil(snapshot, selectedUntil) {
    let until = publicationDateKey(selectedUntil);
    const excludedDays = new Set(validReportingExcludedDays(snapshot));
    if (!until || excludedDays.size === 0) return until ?? selectedUntil;

    const requestedSince = publicationDateKey(snapshot?.requestedSince);
    const date = new Date(`${until}T12:00:00Z`);
    while (excludedDays.has(until)) {
        date.setUTCDate(date.getUTCDate() - 1);
        until = date.toISOString().slice(0, 10);
        if (requestedSince && until < requestedSince) return null;
    }
    return until;
}

/** Returns a contiguous modal/API range after removing excluded dates at either edge. */
export function effectiveReportingRange(snapshot, selectedSince, selectedUntil) {
    let since = publicationDateKey(selectedSince);
    let until = publicationDateKey(selectedUntil);
    if (!since || !until || since > until) return null;

    const excludedDays = new Set(validReportingExcludedDays(snapshot));
    const selectedExcludedDays = [...excludedDays].filter((day) => day >= since && day <= until);
    while (excludedDays.has(since)) {
        const date = new Date(`${since}T12:00:00Z`);
        date.setUTCDate(date.getUTCDate() + 1);
        since = date.toISOString().slice(0, 10);
    }
    while (excludedDays.has(until)) {
        const date = new Date(`${until}T12:00:00Z`);
        date.setUTCDate(date.getUTCDate() - 1);
        until = date.toISOString().slice(0, 10);
    }
    if (since > until || selectedExcludedDays.some((day) => day >= since && day <= until)) return null;
    return {since, until};
}

function publicationDaysBetween(since, until) {
    if (!validPublicationDateKey(since) || !validPublicationDateKey(until) || since > until) return null;
    const days = [];
    const date = new Date(`${since}T12:00:00Z`);
    const end = new Date(`${until}T12:00:00Z`);
    while (date <= end) {
        days.push(date.toISOString().slice(0, 10));
        date.setUTCDate(date.getUTCDate() + 1);
        if (days.length > 10000) return null;
    }
    return days;
}

/** Returns exact published calendar days only when the supplied metadata proves the set. */
export function publishedSnapshotDayKeys(snapshot) {
    return snapshotPublicationDisplay(snapshot).publishedDays;
}

/** Display-only publication dates; this does not change metric availability or financial guards. */
export function snapshotPublicationDisplay(snapshot, options) {
    if (snapshot?.rangeMismatch === true) {
        return {isIncomplete: true, hasMissing: false, missingDays: [], confirmedDataAbsentDays: [], reportingExcludedDays: [], publishedDays: null, requestedSince: null, requestedUntil: null, metadataIncomplete: true};
    }
    const requestedSince = publicationDateKey(snapshot?.requestedSince);
    const requestedUntil = publicationDateKey(snapshot?.requestedUntil);
    const requestDates = publicationDaysBetween(requestedSince, requestedUntil);
    const requestedCountValue = snapshot?.requestedDayCount ?? snapshot?.expectedDayCount;
    const requestedCount = Number.isInteger(requestedCountValue) && requestedCountValue >= 0 ? requestedCountValue : null;
    const publishedCount = Number.isInteger(snapshot?.publishedDayCount) && snapshot.publishedDayCount >= 0
        ? snapshot.publishedDayCount
        : null;
    const suppliedMissing = Array.isArray(snapshot?.missingDays) ? snapshot.missingDays : null;
    const missingDays = suppliedMissing?.map(publicationDateKey) ?? null;
    const currentDayIsUsable = snapshotHasOnlyValidatedCurrentDayPartial(snapshot, options);
    const today = publicationTodayKey(options?.now);
    const displayMissingDays = missingDays === null ? null : missingDays.filter((day) => !(currentDayIsUsable && day === today));
    const suppliedConfirmedAbsent = Array.isArray(snapshot?.confirmedDataAbsentDays) ? snapshot.confirmedDataAbsentDays : [];
    const confirmedDataAbsentDays = [...new Set(suppliedConfirmedAbsent
        .map(publicationDateKey)
        .filter((day) => day && (!requestDates || requestDates.includes(day))))].sort();
    const missingIsValid = missingDays !== null
        && missingDays.every(Boolean)
        && new Set(missingDays).size === missingDays.length
        && (!requestDates || missingDays.every((day) => requestDates.includes(day)));
    const rangeMatchesCount = requestDates !== null
        && requestedCount !== null
        && requestedCount === requestDates.length;
    const effectivePublishedCount = publishedCount === null ? null : publishedCount + (currentDayIsUsable && missingDays?.includes(today) ? 1 : 0);
    const missingIsComplete = missingIsValid
        && publishedCount !== null
        && requestedCount !== null
        && displayMissingDays.length + effectivePublishedCount === requestedCount;
    const reportingExcludedDays = validReportingExcludedDays(snapshot, options).filter((day) => !displayMissingDays?.includes(day));
    const effectiveMissingDays = [...new Set([...(missingIsValid ? displayMissingDays : []), ...reportingExcludedDays])].sort();

    let publishedDays = null;
    if ((snapshot?.complete === true || currentDayIsUsable) && rangeMatchesCount
        && (effectivePublishedCount === null || effectivePublishedCount === requestedCount)
        && (displayMissingDays === null || (missingIsValid && displayMissingDays.length === 0))) {
        publishedDays = requestDates;
    } else if (missingIsComplete && rangeMatchesCount) {
        const missingSet = new Set(displayMissingDays);
        publishedDays = requestDates.filter((day) => !missingSet.has(day));
    }
    if (Array.isArray(publishedDays) && reportingExcludedDays.length > 0) {
        const excludedSet = new Set(reportingExcludedDays);
        publishedDays = publishedDays.filter((day) => !excludedSet.has(day));
    }

    const isIncomplete = isSnapshotPeriodIncomplete(snapshot, options);
    const hasKnownMissingDays = missingIsComplete && displayMissingDays.length > 0;
    const metadataIncomplete = (isIncomplete && !hasKnownMissingDays)
        || (missingDays !== null && missingDays.length > 0 && !missingIsComplete);
    return {
        isIncomplete,
        hasMissing: confirmedDataAbsentDays.length > 0,
        missingDays: effectiveMissingDays,
        confirmedDataAbsentDays,
        reportingExcludedDays,
        publishedDays,
        requestedSince: requestDates ? requestedSince : null,
        requestedUntil: requestDates ? requestedUntil : null,
        metadataIncomplete,
    };
}

/** Joins adjacent dates; a single date is written once. */
export function publicationDateRanges(days) {
    const sorted = [...new Set((Array.isArray(days) ? days : []).map(publicationDateKey).filter(Boolean))].sort();
    const ranges = [];
    for (const day of sorted) {
        const last = ranges.at(-1);
        const nextDay = last ? new Date(`${last[1]}T12:00:00Z`) : null;
        if (nextDay) nextDay.setUTCDate(nextDay.getUTCDate() + 1);
        if (last && nextDay.toISOString().slice(0, 10) === day) last[1] = day;
        else ranges.push([day, day]);
    }
    return ranges.map(([start, end]) => start === end ? formatPublicationDate(start) : `${formatPublicationDate(start)}–${formatPublicationDate(end)}`);
}

export function publicationDateList(days, locale = "cs") {
    return new Intl.ListFormat(locale, {style: "long", type: "conjunction"}).format(publicationDateRanges(days));
}

/** Combines same-selection snapshot metadata conservatively for a shared status banner. */
export function combineSnapshotPublicationDisplay(snapshots, options) {
    const entries = (Array.isArray(snapshots) ? snapshots : [snapshots]).filter(Boolean).map((snapshot) => snapshotPublicationDisplay(snapshot, options));
    const missingDays = [...new Set(entries.flatMap((entry) => entry.missingDays))].sort();
    const reportingExcludedDays = [...new Set(entries.flatMap((entry) => entry.reportingExcludedDays))].sort();
    const confirmedDataAbsentDays = [...new Set(entries.flatMap((entry) => entry.confirmedDataAbsentDays))].sort();
    const canShowPublishedDays = entries.length > 0 && entries.every((entry) => Array.isArray(entry.publishedDays));
    const sameRequestedRange = entries.length > 0
        && entries.every((entry) => entry.requestedSince && entry.requestedUntil
            && entry.requestedSince === entries[0].requestedSince
            && entry.requestedUntil === entries[0].requestedUntil);
    const publishedDays = canShowPublishedDays
        ? entries[0].publishedDays.filter((day) => entries.every((entry) => entry.publishedDays.includes(day)))
        : null;
    return {
        hasMissing: confirmedDataAbsentDays.length > 0,
        hasIncompleteMetadata: entries.some((entry) => entry.metadataIncomplete),
        missingDays,
        confirmedDataAbsentDays,
        reportingExcludedDays,
        publishedDays,
        requestedSince: sameRequestedRange ? entries[0].requestedSince : null,
        requestedUntil: sameRequestedRange ? entries[0].requestedUntil : null,
    };
}

/** Classifies only proven publication shapes for user-facing copy; it does not affect values or availability. */
export function classifyPublicationNotice(display) {
    const requestDates = publicationDaysBetween(display?.requestedSince, display?.requestedUntil);
    const publishedDays = display?.publishedDays;
    const missingDays = display?.missingDays;
    if (!requestDates || !Array.isArray(publishedDays) || !Array.isArray(missingDays)) return {kind: "unknown"};

    const publishedSet = new Set(publishedDays);
    const missingSet = new Set(missingDays);
    if (publishedSet.size + missingSet.size !== requestDates.length
        || requestDates.some((day) => publishedSet.has(day) === missingSet.has(day))) {
        return {kind: "unknown"};
    }

    const firstMissingIndex = requestDates.findIndex((day) => missingSet.has(day));
    if (firstMissingIndex < 0) return {kind: "unknown"};
    const publishedPrefix = requestDates.slice(0, firstMissingIndex);
    const missingSuffix = requestDates.slice(firstMissingIndex);
    const isTail = publishedPrefix.every((day) => publishedSet.has(day))
        && missingSuffix.every((day) => missingSet.has(day));
    if (!isTail) return {kind: "gap"};
    if (publishedPrefix.length === 0) return {kind: "all_missing", from: missingSuffix[0], through: missingSuffix.at(-1)};
    return {kind: "tail", from: missingSuffix[0], through: missingSuffix.at(-1), publishedThrough: publishedPrefix.at(-1)};
}

export function compactPublicationDateRanges(days) {
    const sorted = [...new Set(days)].sort();
    const ranges = [];
    for (const day of sorted) {
        const last = ranges.at(-1);
        const nextDay = last ? new Date(`${last[1]}T12:00:00Z`) : null;
        if (nextDay) nextDay.setUTCDate(nextDay.getUTCDate() + 1);
        if (last && nextDay.toISOString().slice(0, 10) === day) last[1] = day;
        else ranges.push([day, day]);
    }
    return ranges.map(([start, end]) => start === end ? formatPublicationDate(start) : `${formatPublicationDate(start)}–${formatPublicationDate(end)}`);
}

export function snapshotUnavailableShort(snapshot, t, options) {
    const status = classifySnapshotPublication(snapshot, options);
    return status.currentDayPending && !status.hasHistoricalGap
        ? t("reporting_lab.snapshot.waiting_today", {defaultValue: "Čeká na dnešní data"})
        : t("reporting_lab.snapshot.incomplete_short", {defaultValue: "Neúplná data za období"});
}
